/**
 * Zentrale Stammdaten der öffentlichen Website.
 * Kontaktdaten, Navigation und Teamliste hier pflegen — nicht in den Seiten.
 */

/**
 * Öffentliche Adresse der Website — Basis für Canonical-URLs, Social-Vorschauen
 * und strukturierte Daten. Ohne abschließenden Schrägstrich.
 *
 * Achtung: dieselben Stammdaten stehen zusätzlich als JSON-LD in
 * `public/index.html` (dort bewusst statisch, damit Crawler sie ohne
 * JavaScript lesen). Adresse, Telefonnummer und Social-Profile bei Änderungen
 * an beiden Stellen nachziehen.
 */
export const SITE_URL = 'https://www.sb-antczak.de';

export const CONTACT = {
  company: 'Seniorenbetreuung Antczak',
  /**
   * Die oeffentliche Anschrift: das Beratungsbuero im Steinweg 66, seit dem
   * 22.09.2026 der Anker fuer Name, Adresse und Telefonnummer ueberall — Fusszeile,
   * Kontaktseite, JSON-LD, llms.txt, Unternehmensprofil, Branchenverzeichnisse.
   * Weicht eine dieser Stellen ab, gleicht Google die Firma schlechter ab.
   *
   * Nicht dieselbe wie die Anschrift im Impressum: Der Geschaeftssitz bleibt die
   * Celler Str. 63, wo die Verwaltung sitzt — siehe LEGAL.address.
   */
  street: 'Steinweg 66',
  zip: '38518',
  city: 'Gifhorn',
  get address() {
    return `${this.street}, ${this.zip} ${this.city}`;
  },
  phoneLabel: '05371 – 75 00 111',
  phoneHref: 'tel:+4953717500111',
  whatsappLabel: '01556 – 7550001',
  whatsappHref: 'https://wa.me/4915567550001',
  email: 'info@sb-antczak.de',
  get emailHref() {
    return `mailto:${this.email}`;
  },
  get directionsHref() {
    return directionsUrl(this.address);
  },
} as const;

/**
 * Anfahrt in Google Maps, mit dem Büro als Ziel und ohne festen Startpunkt —
 * den setzt Google Maps auf den Standort des Besuchers.
 *
 * Bewusst die Adresse als Ziel und nicht die Koordinaten: Die Adresse pflegt der
 * Kunde im Bearbeiten-Modus, und ein Umzug soll die Anfahrt mitnehmen. Vorher
 * standen hier die Koordinaten des jetzigen Büros fest im Code — nach einem Umzug
 * hätte die Karte die neue Adresse gezeigt und der Link zur alten geführt.
 *
 * `api=1` ist Googles dokumentiertes Adressschema; es öffnet auf dem Telefon die
 * App und am Rechner die Website.
 */
export function directionsUrl(address: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

/**
 * Rechtliche Stammdaten für Impressum und Datenschutzerklärung.
 *
 * `null` bedeutet „liegt nicht vor bzw. ist nicht bekannt". Angaben, die das
 * Gesetz nur „soweit vorhanden" verlangt (Register, USt-IdNr.), werden dann
 * schlicht weggelassen. Pflichtangaben, die noch fehlen, erscheinen auf der
 * Seite als sichtbarer Hinweis — lieber auffällig unfertig als still falsch.
 */
export const LEGAL = {
  /** Einzelunternehmen: der Inhaber muss mit vollem Namen genannt werden. */
  owner: 'Roman Antczak',
  legalForm: 'Einzelunternehmen',

  /**
   * Anschrift fuer das Impressum — bewusst getrennt von CONTACT.
   *
   * Oeffentlich tritt die Firma seit dem 22.09.2026 unter dem Beratungsbuero im
   * Steinweg 66 auf. Der Geschaeftssitz ist davon unberuehrt die Celler Str. 63,
   * wo die Verwaltung sitzt; § 5 DDG verlangt genau diese Anschrift, nicht die
   * Adresse, unter der am meisten geworben wird.
   *
   * Zieht der Sitz irgendwann wirklich um, ist das hier eine Zeile — und erst
   * dann duerfen beide Anschriften wieder zusammenfallen.
   */
  address: { street: 'Celler Str. 63', zip: '38518', city: 'Gifhorn' },

  /**
   * Einzelunternehmen stehen nie im Handelsregister B — das ist den
   * Kapitalgesellschaften vorbehalten. Nur wer als eingetragener Kaufmann
   * firmiert (Zusatz „e.K."), steht in Abteilung A. Beides trifft hier nach
   * aktuellem Kenntnisstand nicht zu.
   */
  registerCourt: null as string | null,
  registerNumber: null as string | null,

  /**
   * Übernommen aus dem Impressum der laufenden Seite (sb-antczak.de/impressum,
   * abgerufen am 28.08.2026). Dort in dieser Schreibweise mit Leerzeichen.
   */
  vatId: 'DE 294 748 027' as string | null,

  /**
   * Institutionskennzeichen — die Kennung, unter der Leistungserbringer mit den
   * Pflege- und Krankenkassen abrechnen. Keine Pflichtangabe nach § 5 DDG, steht
   * aber im Impressum der laufenden Seite und ist für Angehörige ein Beleg, dass
   * über die Pflegekasse abgerechnet werden kann.
   */
  institutionsKennzeichen: '460309193' as string | null,

  /**
   * Anerkennende Behörde nach § 45a SGB XI — **freiwillige Angabe**, kein
   * Pflichtfeld.
   *
   * § 5 Abs. 1 Nr. 3 DDG verlangt die Aufsichtsbehörde nur, „soweit der Dienst
   * im Rahmen einer Tätigkeit angeboten oder erbracht wird, die der
   * behördlichen Zulassung bedarf". Hauswirtschaft, Alltagsbegleitung und die
   * Vermittlung von Betreuungskräften sind das nicht — Gewerbeanmeldung genügt.
   * Die Anerkennung nach § 45a ist keine Zulassung für die Tätigkeit, sondern
   * die Voraussetzung dafür, dass die Pflegekasse zahlt; ohne sie dürfte
   * dieselbe Leistung erbracht, nur nicht über den Entlastungsbetrag
   * abgerechnet werden. Zulassungspflichtig wäre ein ambulanter Pflegedienst
   * mit Versorgungsvertrag nach § 72 SGB XI — das ist der Betrieb nicht, die
   * Pflegedienste stehen auf der Seite als Partner.
   *
   * Ist der Wert `null`, entfällt der Abschnitt im Impressum ersatzlos — ein
   * sichtbarer „fehlt noch"-Kasten stünde dort für eine Angabe, die gar nicht
   * geschuldet ist.
   *
   * Genannt wird sie trotzdem: Die Kostenseite wirbt mit der Abrechnung über
   * den Entlastungsbetrag, und Angehörige prüfen das. Anschrift und
   * Aktenzeichen des Bescheids fehlen noch (Punkt A2 in
   * ops/KUNDENFRAGEN-RELAUNCH.md); der Name allein genügt für den Zweck.
   */
  supervisoryAuthority: 'Niedersächsisches Landesamt für Soziales, Jugend und Familie' as
    | string
    | null,

  /** Datenschutzbeauftragte(r), sofern benannt (§ 38 BDSG ab 20 Personen). */
  dataProtectionOfficer: null as string | null,

  /** Letzte inhaltliche Überarbeitung der Rechtstexte. */
  lastUpdated: 'August 2026',
} as const;

/** Beratungsbüro Steinweg 66, Gifhorn (Quelle: OpenStreetMap, Nominatim). */
export const OFFICE_COORDS = { lat: 52.4843356, lon: 10.5463539 } as const;

/**
 * Social-Profile. Ein YouTube-Kanal ließ sich nicht auffinden — sobald die URL
 * bekannt ist, hier ergänzen; das Icon erscheint dann automatisch im Footer.
 *
 * Dieselben Adressen stehen als `sameAs` im JSON-LD in `public/index.html`:
 * Damit ordnet Google die Profile derselben Firma zu. Änderungen an beiden
 * Stellen nachziehen, sonst zeigt das Markup auf ein totes Profil.
 *
 * Nicht per Statuscode prüfbar: Instagram antwortet auch für nicht existierende
 * Profile mit 200 und liefert die App-Hülle aus. Wer eine Adresse ändert, muss
 * sie im Browser aufrufen.
 */
export const SOCIAL: Array<{ label: string; href: string }> = [
  { label: 'Facebook', href: 'https://www.facebook.com/SeniorenbetreuungAntczak' },
  { label: 'Instagram', href: 'https://www.instagram.com/seniorenbetreuung.antczak/' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/seniorenbetreuung-antczak/' },
  { label: 'WhatsApp', href: CONTACT.whatsappHref },
];

export const NAV = [
  { to: '/aktuelles', label: 'Aktuelles' },
  { to: '/24h-betreuung', label: '24H Betreuung' },
  { to: '/hauswirtschaft', label: 'Hauswirtschaft' },
  { to: '/kosten', label: 'Kosten' },
  { to: '/ueber-uns', label: 'Über uns' },
  { to: '/kontakt', label: 'Kontakt' },
] as const;

export interface TeamMember {
  name: string;
  role: string;
  image: string;
}

export const TEAM: TeamMember[] = [
  { name: 'Roman Antczak', role: 'Inhaber', image: '/website/img/team-roman.jpg' },
  { name: 'Ilona Zdrojewska', role: 'Leitung „24h-Betreuung“', image: '/website/img/team-ilona.jpg' },
  { name: 'Ina Bach', role: 'Pflegedienstleitung', image: '/website/img/team-ina.jpg' },
  { name: 'Viola Milosch', role: 'Kundenbetreuung', image: '/website/img/team-viola.jpg' },
  { name: 'Daria Mosakowska', role: 'Kundenbetreuung & Partner', image: '/website/img/team-daria.jpg' },
  { name: 'Kamil Walter-Poplawski', role: 'Leitung Marketing', image: '/website/img/team-kamil.jpg' },
  { name: 'Dyzio', role: 'Bürohund & Stressabbau', image: '/website/img/team-dyzio.jpg' },
];

/** Die Beiträge der Seite „Aktuelles“ stehen in `newsData.ts`. */

export const ANTCZAK_GROUP = [
  { name: 'Seniorenbetreuung Antczak', image: '/website/img/group-seniorenbetreuung.jpg' },
  { name: 'Antczak Group', image: '/website/img/group-group.jpg' },
  { name: 'Antczak Care', image: '/website/img/group-care.jpg' },
  { name: 'Antczak Foundation', image: '/website/img/group-foundation.jpg' },
  { name: 'Antczak Care Sharing', image: '/website/img/group-care-sharing.jpg' },
];

export const PARTNERS = [
  { name: 'APS Ambulanter Pflegeservice', image: '/website/img/partner-aps.jpg' },
  { name: 'Häuslicher Pflegedienst Bartels', image: '/website/img/partner-bartels.jpg' },
  { name: 'Bettina Harms GmbH', image: '/website/img/partner-harms.jpg' },
  { name: 'Dachstiftung Diakonie', image: '/website/img/partner-diakonie.jpg' },
  { name: 'MVZ Adel Mahdy', image: '/website/img/partner-mvz.jpg' },
  { name: 'Seniorenheim Wendeburg GmbH Löschmann', image: '/website/img/partner-loeschmann.jpg' },
  { name: 'Tagespflege Rosenwinkel', image: '/website/img/partner-rosenwinkel.jpg' },
  { name: 'Sympatica Tagespflege', image: '/website/img/partner-sympatica.jpg' },
  { name: 'Tagespflege Wundervoll', image: '/website/img/partner-wundervoll.jpg' },
  { name: 'VIVAS RehaMed', image: '/website/img/partner-vivas.jpg' },
];
